import { useEffect, useMemo, useState } from 'react';
import { Alert, Platform, Pressable, View } from 'react-native';
import Constants from 'expo-constants';
import * as Application from 'expo-application';
import * as Updates from 'expo-updates';
import * as Clipboard from 'expo-clipboard';
import { Ionicons } from '@expo/vector-icons';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { SubHeading, Body, Caption } from '@/components/ui/Text';
import { useTheme } from '@/theme';
import { getSentryDsn } from '@/services/errorTracking';
import { isInternalDebugBuild } from '@/services/logger';
import {
  getRevenueCatInitStatus,
  initRevenueCat,
  isRevenueCatPublicSdkKeyEmbedded,
} from '@/services/revenuecat';

function shouldShowBuildDiagnostics(): boolean {
  if (Platform.OS === 'web') return false;
  if (isInternalDebugBuild()) return true;
  return process.env.EXPO_PUBLIC_SHOW_BUILD_DIAGNOSTICS === 'true';
}

function formatDate(d: Date | null): string {
  if (!d) return '—';
  try {
    return d.toISOString();
  } catch {
    return String(d);
  }
}

/**
 * Native binary + EAS Update readout for QA and crash matching (one shell, many OTAs).
 * Hidden on web (no native shell / OTA). Shown on native in dev, staging (`extra.appEnv`),
 * or when EXPO_PUBLIC_SHOW_BUILD_DIAGNOSTICS=true.
 */
export function BuildUpdateDiagnosticsCard() {
  const theme = useTheme();
  const [copied, setCopied] = useState(false);
  const [revenueCatProbe, setRevenueCatProbe] = useState(() => getRevenueCatInitStatus());

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      await initRevenueCat();
      if (!cancelled) setRevenueCatProbe(getRevenueCatInitStatus());
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const lines = useMemo(() => {
    const appEnv = Constants.expoConfig?.extra?.appEnv ?? '—';
    const expoVersion = Constants.expoConfig?.version ?? '—';
    const nativeVersion =
      Platform.OS === 'web' ? '—' : (Application.nativeApplicationVersion ?? '—');
    const nativeBuild =
      Platform.OS === 'web' ? '—' : (Application.nativeBuildVersion ?? '—');

    const sentryDsnPresent = Boolean(getSentryDsn());
    const extraSentry = Constants.expoConfig?.extra as Record<string, unknown> | undefined;
    const sentryInExtra = typeof extraSentry?.sentryDsn === 'string' && extraSentry.sentryDsn.length > 0;

    const manifest = Updates.manifest as Record<string, unknown> | undefined;
    const manifestId =
      (typeof manifest?.id === 'string' && manifest.id) ||
      (typeof (manifest as { metadata?: { updateId?: string } })?.metadata?.updateId === 'string'
        ? (manifest as { metadata: { updateId: string } }).metadata.updateId
        : undefined);

    const rcKeyEmbedded = isRevenueCatPublicSdkKeyEmbedded();

    return {
      text: [
        `appEnv: ${appEnv}`,
        `expo.version (app): ${expoVersion}`,
        `nativeApplicationVersion: ${nativeVersion}`,
        `nativeBuildVersion: ${nativeBuild}`,
        `updates.enabled: ${Updates.isEnabled}`,
        `updates.channel: ${Updates.channel ?? '—'}`,
        `updates.runtimeVersion: ${Updates.runtimeVersion ?? '—'}`,
        `updates.updateId: ${Updates.updateId ?? '—'}`,
        `updates.manifest.id: ${manifestId ?? '—'}`,
        `updates.isEmbeddedLaunch: ${Updates.isEmbeddedLaunch}`,
        `updates.isEmergencyLaunch: ${Updates.isEmergencyLaunch}`,
        `updates.createdAt: ${formatDate(Updates.createdAt)}`,
        `updates.launchDurationMs: ${Updates.launchDuration ?? '—'}`,
        `sentryDsn (runtime resolved): ${sentryDsnPresent ? 'yes' : 'no'}`,
        `extra.sentryDsn (manifest): ${sentryInExtra ? 'yes' : 'no'}`,
        `revenueCat.publicSdkKeyEmbedded: ${rcKeyEmbedded ? 'yes' : 'no'}`,
        `revenueCat.initStatus: ${revenueCatProbe.status}`,
        `revenueCat.initialized: ${revenueCatProbe.initialized}`,
        `revenueCat.lastError: ${revenueCatProbe.lastError ?? '—'}`,
      ].join('\n'),
      rows: [
        { label: 'App environment', value: String(appEnv) },
        { label: 'RevenueCat SDK key embedded', value: rcKeyEmbedded ? 'yes' : 'no' },
        { label: 'RevenueCat init status', value: revenueCatProbe.status },
        { label: 'RevenueCat initialized', value: String(revenueCatProbe.initialized) },
        ...(revenueCatProbe.lastError
          ? ([{ label: 'RevenueCat last error', value: revenueCatProbe.lastError }] as const)
          : []),
        { label: 'Sentry DSN resolved', value: sentryDsnPresent ? 'yes' : 'no' },
        { label: 'extra.sentryDsn in manifest', value: sentryInExtra ? 'yes' : 'no' },
        { label: 'App version (expo.version)', value: String(expoVersion) },
        { label: 'Native version', value: String(nativeVersion) },
        { label: 'Native build', value: String(nativeBuild) },
        { label: 'Updates enabled', value: String(Updates.isEnabled) },
        { label: 'Channel', value: Updates.channel ?? '—' },
        { label: 'Runtime version', value: Updates.runtimeVersion ?? '—' },
        { label: 'Update ID', value: Updates.updateId ?? '—' },
        { label: 'Embedded launch', value: String(Updates.isEmbeddedLaunch) },
        { label: 'Emergency launch', value: String(Updates.isEmergencyLaunch) },
        { label: 'Update createdAt', value: formatDate(Updates.createdAt) },
      ] as const,
    };
  }, [revenueCatProbe]);

  if (!shouldShowBuildDiagnostics()) {
    return null;
  }

  const handleCopy = async () => {
    try {
      await Clipboard.setStringAsync(lines.text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      Alert.alert('Copy failed', 'Could not copy to clipboard.');
    }
  };

  return (
    <Card variant="outlined" style={{ borderColor: theme.colors.borderMuted }}>
      <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: theme.spacing(1) }}>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(1) }}>
          <Ionicons name="information-circle-outline" size={22} color={theme.colors.textSecondary} />
          <SubHeading>Build and update</SubHeading>
        </View>
        <Pressable
          onPress={handleCopy}
          accessibilityRole="button"
          accessibilityLabel={copied ? 'Copied' : 'Copy diagnostics to clipboard'}
          hitSlop={8}
        >
          <Ionicons
            name={copied ? 'checkmark-circle' : 'copy-outline'}
            size={22}
            color={copied ? theme.colors.primary : theme.colors.textSecondary}
          />
        </Pressable>
      </View>
      <Caption color={theme.colors.textSecondary} style={{ marginBottom: theme.spacing(1.5) }}>
        Native shell + current OTA. Paste into bug reports to match Expo and symbolication.
      </Caption>
      <View style={{ gap: theme.spacing(0.75) }}>
        {lines.rows.map((row) => (
          <View key={row.label} style={{ flexDirection: 'row', flexWrap: 'wrap', gap: theme.spacing(0.5) }}>
            <Caption color={theme.colors.textSecondary} style={{ minWidth: 128 }}>
              {row.label}
            </Caption>
            <Body style={{ flex: 1, flexShrink: 1 }} selectable>
              {row.value}
            </Body>
          </View>
        ))}
      </View>
      <View style={{ marginTop: theme.spacing(1.5) }}>
        <Button
          label={copied ? 'Copied' : 'Copy all as text'}
          variant="secondary"
          onPress={handleCopy}
          icon={<Ionicons name="copy-outline" size={18} color={theme.colors.text} />}
        />
      </View>
    </Card>
  );
}
